// components/MaintenanceOverlay.tsx
// 유지보수 모드 오버레이 컴포넌트

'use client';

import { useEffect, useState } from 'react';
import { startPublicSettingsPolling } from '@/lib/public-settings-polling';

interface MaintenanceSettings {
  enabled: boolean;
  template: 'holiday' | 'maintenance' | 'emergency' | 'no_delivery';
  title: string;
  message: string;
  icon: string;
  enabledAt?: string;
}

const ADMIN_API_URL = process.env.NEXT_PUBLIC_ADMIN_API_URL;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

function getMaintenanceStatusUrl() {
  if (!ADMIN_API_URL || typeof window === 'undefined') {
    return '/api/maintenance-status';
  }

  try {
    const adminUrl = new URL(ADMIN_API_URL, window.location.origin);
    const isLocalPage = LOCAL_HOSTS.has(window.location.hostname);
    const isLocalAdmin = LOCAL_HOSTS.has(adminUrl.hostname);

    if (isLocalPage && isLocalAdmin && adminUrl.origin !== window.location.origin) {
      return '/api/maintenance-status';
    }

    return `${ADMIN_API_URL}/api/maintenance-status`;
  } catch {
    return '/api/maintenance-status';
  }
}

export default function MaintenanceOverlay() {
  const [settings, setSettings] = useState<MaintenanceSettings | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const stop = startPublicSettingsPolling(async () => {
      try {
        const response = await fetch(getMaintenanceStatusUrl(), {
          cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!controller.signal.aborted && typeof data.enabled === 'boolean') setSettings(data);
      } catch {
        // 일시적인 실패로 이미 확인한 유지보수 상태를 해제하지 않는다.
      }
    });
    return () => { stop(); controller.abort(); };
  }, []);

  // 로딩 중(settings 없음)이거나 비활성화면 아무것도 렌더링하지 않음
  if (!settings?.enabled) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center">
        {/* 아이콘 */}
        <div className="text-7xl mb-6 animate-bounce">{settings.icon}</div>

        {/* 제목 */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{settings.title}</h1>

        {/* 메시지 */}
        <p className="text-lg text-gray-600 mb-6 leading-relaxed">{settings.message}</p>

        {/* 구분선 */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* 연락처 정보 */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Need assistance?</p>
          <a
            href="tel:+16468385011"
            className="inline-flex items-center justify-center gap-2 text-xl font-bold text-amber-700 hover:text-amber-800 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            (646) 838-5011
          </a>
          <div className="mt-2 text-sm text-gray-500">
            <p className="font-semibold mb-1">Store Hours:</p>
            <p>Monday - Thursday: 10 AM - 10 PM</p>
            <p>Friday - Saturday: 10 AM - 11 PM</p>
            <p>Sunday: 12 PM - 9 PM</p>
          </div>
        </div>

        {/* 이메일 */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm font-semibold text-gray-700 mb-2">Email us:</p>
          <a
            href="mailto:info@amsterwine.com"
            className="text-amber-700 hover:text-amber-800 font-medium transition-colors"
          >
            info@amsterwine.com
          </a>
        </div>

        {/* 매장 주소 */}
        <div className="mt-4">
          <p className="text-sm text-gray-600">
            Amster Wine
            <br />
            475 9th Avenue, Space A
            <br />
            New York, NY 10018
          </p>
        </div>
      </div>
    </div>
  );
}
